import React, { useEffect, useState } from 'react';
import HistoricalRatesWrapper from './HistoricalRatesWrapper';
import { currency_pairs } from '../TradesOfTheDay/CurrencyPairs';
import HistoricalRatesHandler from './HistoricalRatesHandler';

export default function CsvToJsonConverter() {
  const [currencyPair, setCurrencyPair] = useState('');
  const [csvInput, setCsvInput] = useState(
    'date,open,high,low,close,average\n'
  );
  const [dataList, setDataList] = useState([]);
  const [selectedPair, setSelectedPair] = useState(null);
  const [selectedCurrencyPair, setSelectedCurrencyPair] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [addRatesPayload, setAddRatesPayload] = useState({});
  const [getRatesPayload, setGetRatesPayload] = useState({});
  const [responseData, setResponseData] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);

  const requiredFields = ['date', 'open', 'high', 'low', 'close', 'average'];
  const ratesResponseFields = ['date', 'high', 'low', 'ask', 'bid'];

  const getFlagUrl = (currency) =>
    `https://flagcdn.com/w40/${currency.substring(0, 2).toLowerCase()}.png`;

  const validateDateFormat = (date) => {
    const regex = /^\d{4}-(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])$/;
    return regex.test(date);
  };

  const addToList = () => {
    let isInvalidRecord = false;
    const validPair = currency_pairs?.includes(currencyPair);
    const lines = csvInput
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);

    if (!/^[A-Z]{3}\/[A-Z]{3}$/.test(currencyPair)) {
      alert('Currency pair must be in format (e.g., EUR/INR)');
      return;
    } else if (!validPair) {
      alert(`Invalid Currency Pair ${currencyPair}`);
      return;
    } else if (lines.length < 2) {
      alert('Invalid CSV format');
      return;
    }

    const headers = lines[0].split(/\s+|,/).map((header) => header.trim());
    if (!requiredFields.every((field) => headers.includes(field))) {
      alert(
        `Missing required fields: ${requiredFields
          .filter((field) => !headers.includes(field))
          .join(', ')}`
      );
      return;
    }

    const data = lines.slice(1).map((line) => {
      const values = line.split(/\s+|,/).map((value) => value.trim());
      let entry = {};
      headers.forEach((header, index) => {
        if (header === 'date' && !validateDateFormat(values[index])) {
          alert('Invalid date format. please provide date in YYYY-DD-MM');
          isInvalidRecord = true;
        }
        entry[header] = values[index];
      });
      return entry;
    });

    if (isInvalidRecord) {
      return;
    }

    setDataList([...dataList, { currencyPair, data }]);
    setCurrencyPair('');
    setCsvInput('date,open,high,low,close,average\n');
  };

  const confirmDelete = (pair) => {
    setSelectedPair(pair);
  };

  const deletePair = () => {
    setDataList(dataList.filter((item) => item.currencyPair !== selectedPair));
    setSelectedPair(null);
  };

  const initialTabs = [
    {
      name: 'Add historical rates',
      active: true,
    },
    {
      name: 'Get historical rates',
      active: false,
    },
  ];

  const [tabs, setTabs] = useState(initialTabs);

  const setTab = (tab) => {
    const newTabs = tabs.map((t) => {
      return { ...t, active: t.name === tab.name };
    });
    setTabs(newTabs);
  };

  useEffect(() => {
    if (dataList.length > 0) {
      const newPayload = {};
      dataList.forEach((item) => {
        if (Object.keys(newPayload).includes(item.currencyPair)) {
          newPayload[item.currencyPair] = [
            ...newPayload[item.currencyPair],
            ...item.data,
          ];
        } else {
          newPayload[item.currencyPair] = item.data;
        }
      });
      setAddRatesPayload(newPayload);
    } else {
      setAddRatesPayload({});
    }
  }, [dataList]);

  useEffect(() => {
    const [year, month, day] = selectedDate.split('-');
    const formattedDate = `${year}-${day}-${month}`;

    setGetRatesPayload({
      [selectedCurrencyPair]: [formattedDate],
    });

    if (selectedCurrencyPair != '' && selectedDate != '') {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [selectedCurrencyPair, selectedDate]);

  function capitalizeFirstChar(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return (
    <HistoricalRatesWrapper tabs={tabs} setTab={setTab}>
      {tabs[0].active ? (
        <>
          <HistoricalRatesHandler
            payload={addRatesPayload}
            buttonText="Add Historical Rates"
            isGetRates={false}
          />
          <div className="border p-4">
            <input
              className="mb-2 w-full rounded border p-2"
              type="text"
              placeholder="Enter Currency Pair (e.g., EUR/INR)"
              value={currencyPair}
              onChange={(e) => setCurrencyPair(e.target.value.toUpperCase())}
            />
            <div className="flex flex-col gap-y-3">
              <span className="text-xs italic text-gray-600">
                ** Please maintain following date format: YYYY-DD-MM
              </span>
              <span className="text-xs italic text-gray-600">
                ** Do not remove the below headers, continue from below line
                with space seperated values or comma.
              </span>
            </div>
            <textarea
              className="mt-3 mb-2 w-full rounded border p-2"
              rows="4"
              placeholder="Enter CSV values for predefined format"
              value={csvInput}
              onChange={(e) => setCsvInput(e.target.value)}
            ></textarea>
            <button
              className="mt-2 rounded bg-blue-500 px-4 py-2 text-white"
              onClick={addToList}
            >
              Add to List
            </button>
            <div className="mt-4">
              {dataList.map((item, index) => (
                <details key={index} className="mb-2 rounded-lg border p-2">
                  <summary className="flex cursor-pointer items-center justify-between">
                    <span className="flex items-center">
                      <img
                        className={`h-4 w-4 rounded-full`}
                        src={getFlagUrl(item.currencyPair.split('/')[0])}
                        alt={item.currencyPair.split('/')[0]}
                      />
                      <img
                        className={`-ml-[2px] h-4 w-4 rounded-full`}
                        src={getFlagUrl(item.currencyPair.split('/')[1])}
                        alt={item.currencyPair.split('/')[1]}
                      />
                      <p className="ml-2 text-sm font-semibold text-gray-700">
                        {item.currencyPair.replace('/', '→')}
                      </p>
                    </span>
                    {selectedPair === item.currencyPair ? (
                      <span>
                        <button
                          onClick={() => setSelectedPair(null)}
                          className="mr-2 rounded-md border px-2 text-gray-500 shadow"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={deletePair}
                          className="rounded-md border bg-white px-2 text-red-500 shadow"
                        >
                          Confirm
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => confirmDelete(item.currencyPair)}
                        className="text-red-500"
                      >
                        ✖
                      </button>
                    )}
                  </summary>
                  <table className="mt-2 w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-200">
                        {requiredFields.map((field, idx) => (
                          <th key={idx} className="border border-gray-300 p-2">
                            {capitalizeFirstChar(field)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {item.data.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border border-gray-300">
                          {requiredFields.map((field, colIndex) => (
                            <td
                              key={colIndex}
                              className="border border-gray-300 p-2 text-center"
                            >
                              {row[field.toLowerCase()] || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <HistoricalRatesHandler
            payload={getRatesPayload}
            buttonText="Get Historical Rates"
            isGetRates={true}
            setResponseData={setResponseData}
            disable={isDisabled}
          />
          <div className="flex gap-x-3 border p-4">
            <input
              className="mb-2 w-full rounded border p-2"
              type="text"
              placeholder="Enter Currency Pair (e.g., EUR/INR)"
              value={selectedCurrencyPair}
              onChange={(e) =>
                setSelectedCurrencyPair(e.target.value.toUpperCase())
              }
            />
            <input
              className="mb-2 w-full rounded border p-2"
              type="date"
              placeholder="Enter Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div className="mt-4 ml-4">
            {Array.isArray(responseData) &&
              responseData.map((item, index) => (
                <details key={index} className="mb-2 rounded-lg border p-2">
                  <summary className="flex cursor-pointer items-center justify-between">
                    <span className="flex items-center">
                      <img
                        className={`h-4 w-4 rounded-full`}
                        src={getFlagUrl(item.currencyPair.split('/')[0])}
                        alt={item.currencyPair.split('/')[0]}
                      />
                      <img
                        className={`-ml-[2px] h-4 w-4 rounded-full`}
                        src={getFlagUrl(item.currencyPair.split('/')[1])}
                        alt={item.currencyPair.split('/')[1]}
                      />
                      <p className="ml-2 text-sm font-semibold text-gray-700">
                        {item.currencyPair.replace('/', '→')}
                      </p>
                    </span>
                    {selectedPair === item.currencyPair ? (
                      <span>
                        <button
                          onClick={() => getRatesPayload({})}
                          className="mr-2 rounded-md border px-2 text-gray-500 shadow"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={deletePair}
                          className="rounded-md border bg-white px-2 text-red-500 shadow"
                        >
                          Confirm
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setResponseData([])}
                        className="text-red-500"
                      >
                        ✖
                      </button>
                    )}
                  </summary>
                  <table className="mt-2 w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-200">
                        {ratesResponseFields.map((field, idx) => (
                          <th key={idx} className="border border-gray-300 p-2">
                            {capitalizeFirstChar(field)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {item.data.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border border-gray-300">
                          {ratesResponseFields.map((field, colIndex) => (
                            <td
                              key={colIndex}
                              className="border border-gray-300 p-2 text-center"
                            >
                              {row[field.toLowerCase()] || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              ))}
          </div>
        </>
      )}
    </HistoricalRatesWrapper>
  );
}
